/**
 * Notion-style editable tag chips.
 *
 * Props:
 *   - entityType: 'customer' | 'vehicle'
 *   - entityId:   id of the customer/vehicle these tags hang off
 *   - tags:       array of { id, name, color } already attached
 *   - className:  optional wrapper class
 *
 * Behavior:
 *   - Each chip is clickable → opens an inline popover anchored under it
 *     with: rename (input), color picker (9 swatches), Remove from this
 *     record, and (owner-only) Delete tag from shop.
 *   - "+ Add tag" opens an autocomplete popover seeded with the shop
 *     dictionary. Owners get a "Create '<name>'" option when no match;
 *     non-owners can only pick from existing.
 *   - "Delete tag from shop" prompts a confirmation Dialog with cascade
 *     usage counts (customers + vehicles affected).
 *
 * Data flow: the parent passes the current tags (from
 * /api/customers/:id or /api/vehicles/:id) and TanStack Query refetches
 * them on attach/detach/update via invalidation in `src/lib/tags.ts`.
 * No local state for the tag list — single source of truth is the
 * parent.
 *
 * Worker enforces role gates separately; the UI here only governs
 * visibility of the buttons.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Plus, Trash2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useCanManageTags } from '@/lib/auth'
import { cn } from '@/lib/utils'
import { TAG_COLORS, chipClass, chipDotClass } from '@/lib/tag-colors'
import type { TagColor } from '@/lib/tag-colors'
import {
  useAttachTag,
  useDeleteTag,
  useDetachTag,
  useShopTags,
  useUpdateTag,
  type Tag,
  type TagWithUsage,
} from '@/lib/tags'

type EntityType = 'customer' | 'vehicle'

export function EditableTags({
  entityType,
  entityId,
  tags,
  className,
}: {
  entityType: EntityType
  entityId: string
  tags: Tag[]
  className?: string
}) {
  const { t } = useTranslation('tags')
  const canManage = useCanManageTags()

  const [activeChipId, setActiveChipId] = useState<string | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<TagWithUsage | null>(null)

  const dict = useShopTags({ enabled: addOpen || activeChipId !== null })

  const attach = useAttachTag(entityType, entityId)
  const detach = useDetachTag(entityType, entityId)
  const update = useUpdateTag()
  const remove = useDeleteTag()

  const attachedIds = useMemo(() => new Set(tags.map((tg) => tg.id)), [tags])

  function findInDict(id: string): TagWithUsage | undefined {
    return (dict.data ?? []).find((tg) => tg.id === id)
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {tags.map((tag) => (
        <ChipWithMenu
          key={tag.id}
          tag={tag}
          entityType={entityType}
          isOpen={activeChipId === tag.id}
          onOpen={() => setActiveChipId(tag.id)}
          onClose={() => setActiveChipId(null)}
          canManage={canManage}
          onDetach={() => {
            detach.mutate(tag.id)
            setActiveChipId(null)
          }}
          onRename={(name) => {
            if (!name || name === tag.name) {
              setActiveChipId(null)
              return
            }
            update.mutate({ id: tag.id, name })
            setActiveChipId(null)
          }}
          onRecolor={(color) => {
            update.mutate({ id: tag.id, color })
            setActiveChipId(null)
          }}
          onDeleteFromShop={() => {
            const usage = findInDict(tag.id)
            setConfirmDelete(
              usage ?? {
                id: tag.id,
                name: tag.name,
                color: tag.color,
                customer_count: 0,
                vehicle_count: 0,
              },
            )
            setActiveChipId(null)
          }}
        />
      ))}

      <AddTagAffordance
        open={addOpen}
        onOpen={() => setAddOpen(true)}
        onClose={() => setAddOpen(false)}
        attachedIds={attachedIds}
        dictionary={dict.data ?? []}
        canManage={canManage}
        onPickExisting={(tg) => {
          attach.mutate({ tag_id: tg.id })
          setAddOpen(false)
        }}
        onCreate={(name) => {
          attach.mutate({ name })
          setAddOpen(false)
        }}
      />

      {confirmDelete ? (
        <DeleteFromShopConfirm
          tag={confirmDelete}
          isPending={remove.isPending}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => {
            const id = confirmDelete.id
            remove.mutate(id, { onSettled: () => setConfirmDelete(null) })
          }}
        />
      ) : null}

      {/* Inline error surfacing — best-effort, doesn't block the UI. */}
      {(attach.error || detach.error || update.error || remove.error) && (
        <span className="ml-1 text-xs text-red-600">
          {attach.error?.message === 'forbidden'
            ? t('errors.forbidden_create')
            : attach.error?.message === 'tag_name_taken' ||
              update.error?.message === 'tag_name_taken'
            ? t('errors.name_taken')
            : attach.error
            ? t('errors.attach_failed')
            : detach.error
            ? t('errors.detach_failed')
            : t('errors.save_failed')}
        </span>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// ChipWithMenu — one chip + its popover. Anchored beneath the chip.

function ChipWithMenu({
  tag,
  entityType: _entityType, // reserved; future per-entity copy variant
  isOpen,
  onOpen,
  onClose,
  canManage,
  onDetach,
  onRename,
  onRecolor,
  onDeleteFromShop,
}: {
  tag: Tag
  entityType: EntityType
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  canManage: boolean
  onDetach: () => void
  onRename: (newName: string) => void
  onRecolor: (newColor: TagColor) => void
  onDeleteFromShop: () => void
}) {
  const { t } = useTranslation('tags')
  const wrapRef = useRef<HTMLDivElement>(null)
  const [renaming, setRenaming] = useState(false)
  const [draftName, setDraftName] = useState(tag.name)

  // Reset draft when reopening so we start from current name. Pattern A
  // (You Might Not Need an Effect): snapshot the previous `isOpen`/name
  // and in-render setState when the menu transitions open or the tag
  // name shifts while the menu is already open — React discards the
  // current paint and re-renders, no flicker.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen)
  const [prevTagName, setPrevTagName] = useState(tag.name)
  if (isOpen !== prevIsOpen || tag.name !== prevTagName) {
    setPrevIsOpen(isOpen)
    setPrevTagName(tag.name)
    if (isOpen) {
      setDraftName(tag.name)
      setRenaming(false)
    }
  }

  // Outside click closes.
  useEffect(() => {
    if (!isOpen) return
    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [isOpen, onClose])

  return (
    <div ref={wrapRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => (isOpen ? onClose() : onOpen())}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition min-h-[28px]',
          chipClass(tag.color),
        )}
      >
        <span
          className={cn('inline-block size-2 rounded-full', chipDotClass(tag.color))}
        />
        {tag.name}
      </button>

      {isOpen ? (
        <div
          role="menu"
          aria-label={t('chip.menu_label')}
          className="absolute left-0 top-full z-30 mt-1 w-64 rounded-md border border-slate-200 bg-white p-2 shadow-lg"
        >
          {/* Rename row */}
          <div className="px-1">
            {renaming ? (
              <div className="flex items-center gap-1.5">
                <Input
                  autoFocus
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      onRename(draftName.trim())
                    }
                    if (e.key === 'Escape') {
                      setRenaming(false)
                    }
                  }}
                  className="h-8 text-xs"
                />
                <Button
                  size="sm"
                  variant="default"
                  className="h-8 px-2 text-xs"
                  onClick={() => onRename(draftName.trim())}
                >
                  {t('chip.rename_save')}
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => canManage && setRenaming(true)}
                disabled={!canManage}
                className={cn(
                  'flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs',
                  canManage
                    ? 'hover:bg-slate-100 text-slate-700'
                    : 'cursor-not-allowed text-slate-400',
                )}
              >
                {t('chip.rename')}
              </button>
            )}
          </div>

          {/* Color picker row */}
          {canManage ? (
            <div className="px-1 pt-1">
              <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-slate-500">
                {t('chip.change_color')}
              </div>
              <div className="grid grid-cols-9 gap-1 px-2 pb-1">
                {TAG_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    aria-label={t(`colors.${c}` as const)}
                    onClick={() => onRecolor(c)}
                    className={cn(
                      'size-5 rounded-full ring-offset-1 transition hover:ring-2 hover:ring-slate-400',
                      chipDotClass(c),
                      c === tag.color && 'ring-2 ring-slate-700',
                    )}
                  />
                ))}
              </div>
            </div>
          ) : null}

          <div className="my-1 h-px bg-slate-200" />

          <button
            type="button"
            onClick={onDetach}
            className="flex w-full items-center gap-2 rounded px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-100"
          >
            <X className="size-3.5" />
            {t('chip.remove_from_record')}
          </button>

          {canManage ? (
            <button
              type="button"
              onClick={onDeleteFromShop}
              className="flex w-full items-center gap-2 rounded px-3 py-1.5 text-left text-xs text-red-700 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-red-50"
            >
              <Trash2 className="size-3.5" />
              {t('chip.delete_from_shop')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// AddTagAffordance — dashed "+ Add tag" button + autocomplete popover.

function AddTagAffordance({
  open,
  onOpen,
  onClose,
  attachedIds,
  dictionary,
  canManage,
  onPickExisting,
  onCreate,
}: {
  open: boolean
  onOpen: () => void
  onClose: () => void
  attachedIds: Set<string>
  dictionary: TagWithUsage[]
  canManage: boolean
  onPickExisting: (tag: TagWithUsage) => void
  onCreate: (name: string) => void
}) {
  const { t } = useTranslation('tags')
  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')

  // Pattern A: reset query + focus the input the moment the popover
  // transitions open. setState happens in render via the prev-snapshot
  // guard; the focus side-effect lives in a tiny effect since it touches
  // a DOM ref, not React state.
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) setQuery('')
  }
  useEffect(() => {
    if (!open) return
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [open])

  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open, onClose])

  const lowerQuery = query.trim().toLowerCase()
  const filtered = useMemo(() => {
    return dictionary
      .filter((tg) => !attachedIds.has(tg.id))
      .filter((tg) =>
        lowerQuery ? tg.name.toLowerCase().includes(lowerQuery) : true,
      )
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
      .slice(0, 12)
  }, [dictionary, attachedIds, lowerQuery])

  const exactMatch = useMemo(
    () =>
      dictionary.some((tg) => tg.name.toLowerCase() === lowerQuery),
    [dictionary, lowerQuery],
  )
  const showCreate = canManage && lowerQuery.length > 0 && !exactMatch

  function pickFirst() {
    if (showCreate) onCreate(query.trim())
    else if (filtered[0]) onPickExisting(filtered[0])
  }

  return (
    <div ref={wrapRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => (open ? onClose() : onOpen())}
        className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 transition hover:border-slate-400 hover:bg-slate-50 min-h-[28px]"
      >
        <Plus className="size-3" />
        {t('add.button')}
      </button>

      {open ? (
        <div className="absolute left-0 top-full z-30 mt-1 w-64 rounded-md border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-100 p-2">
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  pickFirst()
                }
                if (e.key === 'Escape') onClose()
              }}
              placeholder={t('add.placeholder')}
              className="h-8 text-xs"
            />
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            {showCreate ? (
              <button
                type="button"
                onClick={() => onCreate(query.trim())}
                className="flex w-full items-center gap-2 bg-brand-tint/40 px-3 py-1.5 text-left text-xs font-medium text-brand hover:bg-brand-tint"
              >
                <Plus className="size-3.5" />
                {t('add.create_option', { name: query.trim() })}
              </button>
            ) : null}
            {filtered.map((tg) => (
              <button
                key={tg.id}
                type="button"
                onClick={() => onPickExisting(tg)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-slate-50"
              >
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 font-medium',
                    chipClass(tg.color),
                  )}
                >
                  <span
                    className={cn(
                      'inline-block size-1.5 rounded-full',
                      chipDotClass(tg.color),
                    )}
                  />
                  {tg.name}
                </span>
              </button>
            ))}
            {!showCreate && filtered.length === 0 ? (
              <div className="px-3 py-3 text-xs text-slate-500">
                {t('empty')}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// DeleteFromShopConfirm — confirmation modal with cascade usage.

function DeleteFromShopConfirm({
  tag,
  isPending,
  onCancel,
  onConfirm,
}: {
  tag: TagWithUsage
  isPending: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const { t } = useTranslation('tags')
  const total = tag.customer_count + tag.vehicle_count

  const body =
    total === 0
      ? t('delete_confirm.body_zero')
      : total === 1
      ? t('delete_confirm.body_one')
      : t('delete_confirm.body_other', {
          customer_count: tag.customer_count,
          vehicle_count: tag.vehicle_count,
        })

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('delete_confirm.title', { name: tag.name })}
          </DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isPending}>
            {t('delete_confirm.cancel')}
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isPending}>
            {t('delete_confirm.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
